import type { ColumnDef } from "@tanstack/react-table";
import type { FileItem } from "../../types";
import { cn } from "@/shared/lib/cn";
import { formatRelativeDate } from "../../utils/fileUtils";
import { ThumbnailCell } from "./ThumbnailCell";
import { Checkbox } from "@/shared/ui/checkbox";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";

interface ColumnHelpers {
  onSort?: (column: string) => void;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  getSortIcon: (column: string) => React.ReactNode;
  handleSortClick: (column: string) => void;
  selectedFileIds?: Set<string>;
  onMultiSelect?: (file: FileItem, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
  totalFiles: number;
}

export function createFileGalleryColumns(
  attributeColumns: string[],
  helpers: ColumnHelpers,
  categoryAttributes?: DisplayAttribute[]
): ColumnDef<FileItem>[] {
  const {
    onSort,
    getSortIcon,
    handleSortClick,
    selectedFileIds,
    onMultiSelect,
    onSelectAll,
    totalFiles,
  } = helpers;

  const checkboxColumn: ColumnDef<FileItem> | null = onMultiSelect
    ? {
        id: "select",
        header: () => {
          const isAllSelected =
            totalFiles > 0 && selectedFileIds?.size === totalFiles;
          const isSomeSelected =
            (selectedFileIds?.size || 0) > 0 && !isAllSelected;

          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={
                  isAllSelected
                    ? true
                    : isSomeSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={(checked) => onSelectAll?.(checked === true)}
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const isSelected = selectedFileIds?.has(row.original.id) || false;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked: boolean) =>
                  onMultiSelect(row.original, checked === true)
                }
              />
            </div>
          );
        },
        size: 40,
        minSize: 40,
        enableSorting: false,
      }
    : null;

  const baseColumns: ColumnDef<FileItem>[] = [
    {
      accessorKey: "name",
      header: () => (
        <div
          className={cn(
            "flex items-center",
            onSort && "cursor-pointer hover:opacity-80 select-none"
          )}
          onClick={() => onSort && handleSortClick("name")}
        >
          Name
          {onSort && getSortIcon("name")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink-900 truncate max-w-[200px]">
            {`${row.original.name}${row.original.extension}`}
          </span>
        </div>
      ),
      size: 200,
      minSize: 200,
    },
    {
      accessorKey: "preview",
      header: "Preview",
      cell: ({ row }) => (
        <ThumbnailCell
          thumbnailUrl={row.original.thumbnailUrl}
          fileType={row.original.type}
          fileName={row.original.name}
        />
      ),
      size: 100,
      minSize: 100,
    },
    {
      accessorKey: "extension",
      header: () => (
        <div
          className={cn(
            "flex items-center",
            onSort && "cursor-pointer hover:opacity-80 select-none"
          )}
          onClick={() => onSort && handleSortClick("extension")}
        >
          Format
          {onSort && getSortIcon("extension")}
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm text-ink-600">
          {row.original.extension ||
            row.original.contentType.split("/")[1]?.toUpperCase() ||
            "—"}
        </span>
      ),
      size: 80,
      minSize: 80,
    },
    {
      accessorKey: "size",
      header: () => (
        <div
          className={cn(
            "flex items-center",
            onSort && "cursor-pointer hover:opacity-80 select-none"
          )}
          onClick={() => onSort && handleSortClick("fileSize")}
        >
          Size
          {onSort && getSortIcon("fileSize")}
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm text-ink-600">{row.original.size}</span>
      ),
      size: 100,
      minSize: 100,
    },
    {
      accessorKey: "createdDate",
      header: () => (
        <div
          className={cn(
            "flex items-center",
            onSort && "cursor-pointer hover:opacity-80 select-none"
          )}
          onClick={() => onSort && handleSortClick("createdDate")}
        >
          Created At
          {onSort && getSortIcon("createdDate")}
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm text-ink-600">
          {formatRelativeDate(row.original.createdDate)}
        </span>
      ),
      size: 140,
      minSize: 140,
    },
    {
      accessorKey: "viewCount",
      header: () => (
        <div
          className={cn(
            "flex items-center",
            onSort && "cursor-pointer hover:opacity-80 select-none"
          )}
          onClick={() => onSort && handleSortClick("viewCount")}
        >
          Views
          {onSort && getSortIcon("viewCount")}
        </div>
      ),
      cell: ({ row }) => (
        <span className="text-sm text-ink-600">
          {row.original.viewCount.toLocaleString()}
        </span>
      ),
      size: 80,
      minSize: 80,
    },
  ];

  const mandatoryAttributeMap = new Map(
    (categoryAttributes || []).map((attr) => [attr.name, attr.isRequired])
  );

  const attrColumns: ColumnDef<FileItem>[] = attributeColumns.map(
    (attrName) => ({
      id: `attr-${attrName}`,
      accessorFn: (row) => {
        const attrValue = row.attributeValues?.find(
          (attr) => attr.attributeName === attrName
        )?.value;
        return attrValue || "";
      },
      header: attrName,
      cell: ({ getValue }) => {
        const value = getValue();
        const isEmpty =
          !value || (typeof value === "string" && value.trim() === "");
        const isMandatory = mandatoryAttributeMap.get(attrName) ?? false;
        const displayValue: string = isEmpty
          ? isMandatory
            ? "Missing"
            : "N/A"
          : typeof value === "string"
            ? value
            : String(value || "");

        return (
          <span
            className={
              isEmpty ? "text-sm text-ink-400 italic" : "text-sm text-ink-600"
            }
          >
            {displayValue}
          </span>
        );
      },
      size: 120,
      minSize: 120,
    })
  );

  return checkboxColumn
    ? [checkboxColumn, ...baseColumns, ...attrColumns]
    : [...baseColumns, ...attrColumns];
}
